import {
  HomeTypeEnum,
  ChipEnum,
  Icons,
  OwnershipType,
  SoldFilterParams,
  SoldRefParams,
  SoldWeightParams,
  TagColor,
} from '../../enums';
import {
  FilterMinMaxItem,
  FilterTagItem,
  TagItem,
  WeightItemsType,
  CheckboxItem,
  FilterDateItem,
} from '../../types/dataTypes';
import strings from '../../language/Swedish';

const ownerTypes: TagItem[] = [
  {
    id: OwnershipType.coOperativeApartment,
    label: strings.filter.ownertypes.co_operative,
    color: TagColor.green,
  },
  {
    id: OwnershipType.propertyRights,
    label: strings.filter.ownertypes.property_right,
    color: TagColor.blue,
  },
  {
    id: OwnershipType.siteLeasehold,
    label: strings.filter.ownertypes.site_leasehold,
    color: TagColor.amber,
  },
  {
    id: OwnershipType.lease,
    label: strings.filter.ownertypes.lease,
    color: TagColor.pink,
  },
];

const housingTypes = [
  {
    id: HomeTypeEnum.apartment,
    label: strings.filter.housingTypes.apartment,
    color: TagColor.pink,
  },
  {
    id: HomeTypeEnum.house,
    label: strings.filter.housingTypes.house,
    color: TagColor.green,
  },
  {
    id: HomeTypeEnum.terracedHouse,
    label: strings.filter.housingTypes.terracedHouse,
    color: TagColor.orange,
  },
  {
    id: HomeTypeEnum.townhouse,
    label: strings.filter.housingTypes.townhouse,
    color: TagColor.cyan,
  },
  {
    id: HomeTypeEnum.farm,
    label: strings.filter.housingTypes.farm,
    color: TagColor.indigo,
  },
  {
    id: HomeTypeEnum.plot,
    label: strings.filter.housingTypes.plot,
    color: TagColor.violet,
  },
];

const filterItems: {
  tagItems: FilterTagItem[];
  minMaxItems: FilterMinMaxItem[];
  dateItems: FilterDateItem[];
  checkboxItems: CheckboxItem[];
} = {
  tagItems: [
    {
      id: SoldFilterParams.ownership_type,
      label: strings.filter.owner,
      icon: Icons.key,
      items: ownerTypes,
    },
    {
      id: SoldFilterParams.homeType,
      label: strings.filter.housingType,
      icon: Icons.house,
      items: housingTypes,
    },
  ],
  minMaxItems: [
    {
      minId: SoldFilterParams.rooms_min,
      maxId: SoldFilterParams.rooms_max,
      label: strings.filter.rooms,
      icon: Icons.bed,
      valueLabel: '',
      min: 0,
      max: 10,
    },
    {
      minId: SoldFilterParams.living_area_min,
      maxId: SoldFilterParams.living_area_max,
      label: strings.filter.living_area,
      icon: Icons.area,
      valueLabel: 'm\u00B2',
      min: 0,
      max: 500,
    },
    {
      minId: SoldFilterParams.price_min,
      maxId: SoldFilterParams.price_max,
      label: strings.filter.price,
      icon: Icons.coins,
      valueLabel: 'mkr',
      min: 0,
      max: 40,
      type: 'decimal',
    },
    {
      minId: SoldFilterParams.monthly_fee_min,
      maxId: SoldFilterParams.monthly_fee_max,
      label: strings.filter.monthly_fee,
      icon: Icons.dollar,
      valueLabel: 'SEK',
      min: 0,
      max: 12000,
    },
    {
      minId: SoldFilterParams.floor_min,
      maxId: SoldFilterParams.floor_max,
      label: strings.filter.floor,
      icon: Icons.floors,
      valueLabel: '',
      min: 0,
      max: 20,
    },
    {
      minId: SoldFilterParams.construction_year_min,
      maxId: SoldFilterParams.construction_year_max,
      label: strings.filter.construction_year,
      icon: Icons.crane,
      valueLabel: '',
      min: 1800,
      max: 2024,
    },
    {
      minId: SoldFilterParams.shore_distance_min,
      maxId: SoldFilterParams.shore_distance_max,
      label: strings.filter.shore,
      icon: Icons.shore,
      tooltip: strings.table.tooltip.shoreDistanceType,
      valueLabel: '',
      min: 1,
      max: 4,
    },
  ],
  dateItems: [
    {
      minId: SoldFilterParams.contract_date_min,
      maxId: SoldFilterParams.contract_date_max,
      label: strings.filter.contract_date,
      icon: Icons.calendar,
      valueLabel: '',
      startDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
      endDate: new Date(),
    },
  ],
  checkboxItems: [
    {
      id: 'firm',
      label: strings.filter.checkboxItems.firm,
    },
    {
      id: 'orgNr', //SoldFilterParams.orgNr,
      label: strings.filter.checkboxItems.orgNr,
    },
    {
      id: SoldFilterParams.is_new_construction,
      label: strings.filter.checkboxItems.showNewBuilds,
    },
    {
      id: SoldFilterParams.error_code,
      label: strings.filter.checkboxItems.inadequateData,
    },
  ],
};

const weightItems: WeightItemsType[] = [
  {
    icon: Icons.handShake,
    keyWeightEl: SoldWeightParams.contract_date_weight,
    min: 0,
    max: 1000,
    label: strings.filter.contract_date,
    inputType: ChipEnum.select,
  },
  {
    icon: Icons.pin,
    keyWeightEl: SoldWeightParams.distance_weight,
    min: 0,
    max: 1000,
    label: strings.filter.distance,
    inputType: ChipEnum.select,
  },
  {
    icon: Icons.bed,
    keyWeightEl: SoldWeightParams.rooms_weight,
    keyRefEl: SoldRefParams.rooms_reference,
    min: 0,
    max: 10,
    weight: 500,
    label: strings.filter.rooms,
    inputType: ChipEnum.slider,
  },
  {
    icon: Icons.area,
    keyWeightEl: SoldWeightParams.living_area_weight,
    keyRefEl: SoldRefParams.living_area_reference,
    min: 0,
    max: 500,
    weight: 500,
    label: strings.filter.living_area,
    inputType: ChipEnum.slider,
    unit: strings.filter.living_area_unit,
  },
  {
    icon: Icons.dollar,
    keyWeightEl: SoldWeightParams.monthly_fee_weight,
    keyRefEl: SoldRefParams.monthly_fee_reference,
    min: 0,
    max: 15000,
    weight: 500,
    label: strings.filter.monthly_fee,
    inputType: ChipEnum.slider,
    unit: strings.filter.monthly_fee_unit,
  },
  {
    icon: Icons.crane,
    keyWeightEl: SoldWeightParams.construction_year_weight,
    keyRefEl: SoldRefParams.construction_year_reference,
    min: 1800,
    max: 2024,
    weight: 500,
    label: strings.filter.construction_year,
    inputType: ChipEnum.slider,
  },
  {
    icon: Icons.apartment,
    keyWeightEl: SoldWeightParams.floor_weight,
    keyRefEl: SoldRefParams.floor_reference,
    min: 0,
    max: 75,
    weight: 500,
    label: strings.filter.floor,
    inputType: ChipEnum.slider,
  },
];

export { weightItems, filterItems };
